import React from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import DemoExam from './components/DemoExam/DemoExam';
import { useLanguage, LanguageProvider } from './i18n/LanguageContext';
import LanguageSelector from './components/LanguageSelector/LanguageSelector';
import SEO from './components/SEO';
import './App.css';

function LandingPage() {
  const { t, language } = useLanguage();
  const langPrefix = language === 'en' ? '' : `/${language}`;
  
  const copyToClipboard = (text, originalText, event) => {
    const button = event.currentTarget;
    if (!button) return;
    
    navigator.clipboard.writeText(text).then(() => {
      button.classList.add('copied');
      button.innerHTML = '✓ Copied!';
      setTimeout(() => {
        button.classList.remove('copied');
        button.innerHTML = originalText;
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };
  
  return (
    <div className="app">
      <SEO 
        language={language}
        title={t('pageTitle')}
        description={t('heroSubtitle')}
      />
      {/* Hero Section */}
      <header className="hero">
        <div className="container">
          <div className="logo">
            <span className="logo-text">PublicQ</span>
            <span className="free-badge">FREE</span>
            <div className="language-selector-wrapper">
              <LanguageSelector />
            </div>
          </div>
          <h1 className="hero-title">{t('heroTitle')}</h1>
          <p className="hero-subtitle">
            {t('heroSubtitle')}
          </p>
          <div className="hero-buttons">
            <Link to={`${langPrefix}/demo`} className="button button-primary button-demo">
              {t('oneClickDemo')}
              <span className="button-demo-icon">
                <img src="/images/icons/rocket-48.png" alt="rocket" style={{ width: '24px', height: '24px' }} />
              </span>
            </Link>
            <a 
              href="#platform-access" 
              className="button button-platform"
            >
              {t('fullPlatformAccess')}
              <img src="/images/icons/test-96.png" alt="platform" style={{ width: '20px', height: '20px', marginLeft: '8px' }} />
            </a>
            <a href="#features" className="button button-secondary">{t('learnMore')}</a>
            <a 
              href="https://github.com/MTokarev/publicq" 
              className="button button-github"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('viewOnGitHub')}
              <img src="/images/icons/github-96.png" alt="github" style={{ width: '20px', height: '20px', marginLeft: '8px', filter: 'brightness(0) invert(1)' }} />
            </a>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <h2 className="section-title">{t('everythingYouNeed')}</h2>
          <p className="section-subtitle">
            {t('comprehensiveSolution')}
          </p>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/test-96.png" alt="assessment" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('easyAssessmentCreation')}</h3>
              <p>{t('easyAssessmentDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/version-94.png" alt="version control" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('versionControl')}</h3>
              <p>{t('versionControlDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/users-96.png" alt="users" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('smartUserManagement')}</h3>
              <p>{t('smartUserManagementDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/analytics-96.png" alt="analytics" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('detailedAnalytics')}</h3>
              <p>{t('detailedAnalyticsDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/bot-94.png" alt="AI bot" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('aiPoweredAssistance')}</h3>
              <p>{t('aiPoweredAssistanceDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/time-machine-96.png" alt="real-time" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('realTimeExecution')}</h3>
              <p>{t('realTimeExecutionDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/cellphone-100.png" alt="mobile" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('mobileFriendly')}</h3>
              <p>{t('mobileFriendlyDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/user-shield-96.png" alt="security" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('secureReliable')}</h3>
              <p>{t('secureReliableDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/notification-96.png" alt="notifications" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('communicationTools')}</h3>
              <p>{t('communicationToolsDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/settings-96.png" alt="settings" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('flexibleConfiguration')}</h3>
              <p>{t('flexibleConfigurationDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/delivery-96.png" alt="deployment" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('easyDeployment')}</h3>
              <p>{t('easyDeploymentDesc')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <img src="/images/icons/api-96.png" alt="API" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('apiIntegration')}</h3>
              <p>{t('apiIntegrationDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases">
        <div className="container">
          <h2 className="section-title">{t('perfectFor')}</h2>
          <div className="use-cases-grid">
            <div className="use-case">
              <div className="use-case-icon">
                <img src="/images/icons/university-96.png" alt="education" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('educationalInstitutions')}</h3>
              <p>{t('educationalInstitutionsDesc')}</p>
            </div>
            <div className="use-case">
              <div className="use-case-icon">
                <img src="/images/icons/office-96.png" alt="corporate" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('corporateTraining')}</h3>
              <p>{t('corporateTrainingDesc')}</p>
            </div>
            <div className="use-case">
              <div className="use-case-icon">
                <img src="/images/icons/learning-96.png" alt="learning" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('onlineLearning')}</h3>
              <p>{t('onlineLearningDesc')}</p>
            </div>
            <div className="use-case">
              <div className="use-case-icon">
                <img src="/images/icons/certification-96.png" alt="certification" style={{ width: '48px', height: '48px' }} />
              </div>
              <h3>{t('certificationPrograms')}</h3>
              <p>{t('certificationProgramsDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Access Section */}
      <section id="platform-access" className="platform-access">
        <div className="container">
          <h2 className="section-title">{t('tryFullPlatform')}</h2>
          <p className="section-subtitle">
            {t('platformAccessDesc')}
          </p>
          <div className="platform-access-content">
            <div className="platform-features">
              <div className="platform-icon">
                <img src="/images/icons/test-96.png" alt="platform" style={{ width: '64px', height: '64px' }} />
              </div>
              <h3 className="platform-steps-title">{t('howToMakeTestAvailable')}</h3>
              <div className="platform-steps">
                <div className="platform-step">
                  <span className="step-number">1</span>
                  <div className="step-content">
                    <p className="step-text">{t('platformStep1')}</p>
                    <p className="step-example">{t('platformStep1Example')}</p>
                  </div>
                </div>
                <div className="platform-step">
                  <span className="step-number">2</span>
                  <div className="step-content">
                    <p className="step-text">{t('platformStep2')}</p>
                    <p className="step-example">{t('platformStep2Example')}</p>
                  </div>
                </div>
                <div className="platform-step">
                  <span className="step-number">3</span>
                  <div className="step-content">
                    <p className="step-text">{t('platformStep3')}</p>
                    <p className="step-example">{t('platformStep3Example')}</p>
                  </div>
                </div>
                <div className="platform-step-final">
                  <p>{t('platformStep4')}</p>
                </div>
              </div>
            </div>
            <div className="credentials-box">
              <h3>{t('demoCredentials')}</h3>
              <div className="credential-item">
                <span className="credential-label">{t('username')}:</span>
                <button 
                  className="credential-value credential-copy"
                  onClick={(e) => copyToClipboard('manager@publicq.app', 'manager@publicq.app', e)}
                  title="Click to copy"
                >
                  manager@publicq.app
                </button>
              </div>
              <div className="credential-item">
                <span className="credential-label">{t('password')}:</span>
                <button 
                  className="credential-value credential-copy"
                  onClick={(e) => copyToClipboard('Manager123', 'Manager123', e)}
                  title="Click to copy"
                >
                  Manager123
                </button>
              </div>
              <a 
                href="https://dev.publicq.app" 
                className="button button-primary button-large button-access"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('accessPlatform')}
                <img src="/images/icons/test-96.png" alt="access" style={{ width: '24px', height: '24px', marginLeft: '8px' }} />
              </a>
              <div className="manager-disclaimer">
                <p>{t('managerRoleDisclaimer')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Open Source Section */}
      <section className="open-source">
        <div className="container">
          <div className="open-source-content">
            <div className="open-source-icon">
              <img src="/images/icons/rocket-96.png" alt="rocket" style={{ width: '64px', height: '64px' }} />
            </div>
            <h2>{t('freeOpenSource')}</h2>
            <p>
              {t('freeOpenSourceDesc')}
            </p>
            <div className="tech-stack">
              <span className="tech-badge">.NET 10</span>
              <span className="tech-badge">React 19</span>
              <span className="tech-badge">TypeScript</span>
              <span className="tech-badge">SQLite/PostgreSQL</span>
              <span className="tech-badge">Redis</span>
              <span className="tech-badge">Docker</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>{t('needCustomFeatures')}</h2>
          <p>
            {t('needCustomFeaturesDesc')}
          </p>
          <div className="cta-buttons">
            <a href="mailto:publicq-app@outlook.com" className="button button-primary button-large">
              {t('contactUs')}
            </a>
          </div>
          <p className="cta-note">
            {t('customizeNote')}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>PublicQ</h3>
              <p>{t('publicqDesc')}</p>
            </div>
            <div className="footer-section">
              <h4>{t('quickLinks')}</h4>
              <ul>
                <li><a href="#features">{t('features')}</a></li>
                <li><a href="https://github.com/MTokarev/publicq" target="_blank" rel="noopener noreferrer">GitHub</a></li>
                <li><a href="https://github.com/MTokarev/publicq/blob/main/README.MD" target="_blank" rel="noopener noreferrer">{t('documentation')}</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>{t('contact')}</h4>
              <p>
                <a href="mailto:publicq-app@outlook.com">publicq-app@outlook.com</a>
              </p>
              <p className="footer-note">{t('forCustomFeatures')}</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{t('iconsBy')} <a href="https://icons8.com" target="_blank" rel="noopener noreferrer">Icons8</a></p>
            <p>&copy; {new Date().getFullYear()} {t('copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <Routes>
          {/* English routes (default) */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/demo" element={<DemoExam />} />
          
          {/* Russian routes */}
          <Route path="/ru" element={<LandingPage />} />
          <Route path="/ru/demo" element={<DemoExam />} />
          
          {/* Hindi routes */}
          <Route path="/hi" element={<LandingPage />} />
          <Route path="/hi/demo" element={<DemoExam />} />
          
          {/* Chinese routes */}
          <Route path="/zh" element={<LandingPage />} />
          <Route path="/zh/demo" element={<DemoExam />} />
        </Routes>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
